/**
 * RegionCoverage.jsx — 방문빈도 지역 관리 메인 컴포넌트
 *
 * 마케터들의 최근 N일 지역별 방문 현황을 Leaflet 지도 위 버블 마커로 시각화합니다.
 * 데스크탑: 지도 + 순위 목록 side-by-side
 * 모바일: 지도 탭 / 목록 탭 전환 방식
 *
 * 분리된 모듈:
 *   helpers.js       — 상수, 색상/크기/아이콘/타임라인/겹침 헬퍼 함수
 *   RegionList.jsx   — 방문 기회도 순위 목록 컴포넌트
 *   useRegionMap.js  — Leaflet 지도 초기화·마커·오버레이 로직 훅
 *   useRegionCoverageData.js — 불러오기(관리 권한·색상 기준·방문 집계) (2026-10-29 떼어냄)
 *
 * 2026-10-29 분류 버튼: 같은 분류로 남이 최근 간 곳을 피하려는 화면이라 [전체]+장부 분류로 거른다(미남님).
 *   api(통합 메뉴 '방문빈도 관리'): { coverageUrl, rulesUrl, manageKeys } — 같은 화면을 주소만 바꿔 쓴다.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import 'leaflet/dist/leaflet.css';
import { DEFAULT_PERIOD } from './helpers';
import RegionList from './RegionList';
import { useRegionMap } from './useRegionMap';
import { stdOutlineBtn, stdTabGroup, stdTabBtn } from '../adminlayout/designStandard';
import { RegionColorSettingsModal } from './RegionColorSettingsModal';
import { RegionLegend } from './RegionLegend';
import { useRegionCoverageData } from './useRegionCoverageData';

// 🗺️ 버튼(position:fixed) = 지도 위쪽에서 이만큼 아래 — 예전 고정값 172 일 때의 자리(지도 위 7px)
const MAP_BTN_OFFSET = 7;

export default function RegionCoverage({ adminMode = false, api = null }) {
  // ⚙️ 설정(지역 색상 기준) 창 열림 — 관리 권한(canManage)·불러오기는 useRegionCoverageData
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mobileTab,   setMobileTab]  = useState('map'); // 'map' | 'list'
  const {
    canManage, regions, loading, colorRules, fetchColorRules, categories, category, selectCategory,
  } = useRegionCoverageData({ adminMode, ...(api || {}) });

  // 화면 너비 → 모바일 여부 (resize 이벤트 감지 — 화면 회전 시 레이아웃 전환)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const isMobileRef = useRef(isMobile);
  useEffect(() => {
    function onResize() {
      const next = window.innerWidth <= 768;
      if (next !== isMobileRef.current) { isMobileRef.current = next; setIsMobile(next); }
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── Leaflet 지도 로직 훅 ─────────────────────────────────────────────────────
  const {
    mapContainerRef,
    overlayDivRef,
    imgTransform,
    highlightedRegion,
    mapOverlayActive,
    toggleOverlay,
    handleListClick,
    handleReset,
  } = useRegionMap({ regions, colorRules, mobileTab, setMobileTab, isMobile });

  // 🗺️ 버튼 높이 — 화면마다 머리 높이가 달라(직원·관리·통합 메뉴, 분류 줄) 지도 위쪽을 재서 맞춘다(2026-10-29, 예전 고정 172)
  const [mapBtnTop, setMapBtnTop] = useState(172);
  useLayoutEffect(() => {
    if (!isMobile) return undefined;
    const measure = () => {
      const r = mapContainerRef.current?.getBoundingClientRect();
      if (r && r.height > 0) setMapBtnTop(Math.round(r.top) + MAP_BTN_OFFSET);
    };
    measure();
    // 머리 줄이 늦게 커지면(⚙️·분류 버튼이 나중에 그려짐) 지도 영역이 줄어든다 — 그때마다 다시 잰다
    const ro = typeof ResizeObserver === 'function' && mapContainerRef.current ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(mapContainerRef.current);
    window.addEventListener('resize', measure);
    return () => { window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
  }, [isMobile, mapContainerRef]);

  // ── 공통 UI 요소 ─────────────────────────────────────────────────────────────

  // 전국 지도로 돌아가기 — 미팅지도 ↺ 와 같은 모양(테두리 없는 큰 화살표), 제목 바로 오른쪽(미남님 2026-10-30).
  //   예전엔 오른쪽 위 글자 버튼 '전체'였다 — 분류 [전체]와 이름이 겹쳐 바꿨다. 하는 일은 그대로(강조 풀고 전국 보기).
  const ResetButton = (
    <button type="button" onClick={handleReset} title="전국 지도로 돌아가기" aria-label="전국 지도로 돌아가기"
      style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '2px 4px', lineHeight: 1,
        display: 'inline-flex', alignItems: 'center', fontSize: 20, color: 'var(--text)', flexShrink: 0 }}>
      ↺
    </button>
  );

  // 분류 버튼 — [전체] 맨 앞 + 장부 분류(다나가 순). '분류' 글자 없이(미남님 2026-10-30 — 휴대폰에서 지도·목록과 한 줄).
  const CategoryTabs = (
    <div style={{ ...stdTabGroup, overflowX: 'auto', minWidth: 0, maxWidth: '100%' }}>
      {[{ key: '', label: '전체' }, ...categories.map((c) => ({ key: c, label: c }))].map(({ key, label }) => (
        <button key={key || '__all__'} type="button" aria-pressed={category === key}
          onClick={() => selectCategory(key)} style={stdTabBtn(category === key)}>
          {label}
        </button>
      ))}
    </div>
  );

  // 제목 + ↺ (PC·휴대폰 같은 모양)
  const titleWithReset = (size) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
      <h2 style={{ margin: 0, fontSize: size, fontWeight: 800, color: 'var(--text)' }}>{adminMode ? '방문빈도 관리' : '방문빈도'}</h2>
      {ResetButton}
    </div>
  );

  /**
   * 지도 DOM 컨테이너 — 항상 마운트, 탭 전환 시 visibility로 제어
   * data-nopull="true": AdminLayout pull-to-refresh가 지도에서 발동되지 않도록 차단
   */
  const MapContainer = (
    <div ref={mapContainerRef} data-nopull="true" style={{ position: 'absolute', inset: 0 }} />
  );

  // ── 데스크탑 레이아웃 ────────────────────────────────────────────────────────

  if (!isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 40px)', gap: 0, overflow: 'hidden' }}>

        {/* 헤더: 제목 ↺ + 분류 + 범례 / 오른쪽 ⚙️ */}
        <div style={{
          flexShrink: 0, background: 'var(--bg-card)', borderRadius: 10, padding: '11px 16px', marginBottom: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
          border: '1px solid var(--border)', boxShadow: 'none',
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', margin: '0 0 6px' }}>
              {titleWithReset(20)}
              {CategoryTabs}
            </div>
            <RegionLegend />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {adminMode && canManage && (
              <button type="button" style={stdOutlineBtn} onClick={() => setSettingsOpen(true)}
                title="지역 색상 기준 설정" aria-label="방문빈도 설정">⚙️ 설정</button>
            )}
            {loading && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>로딩 중...</span>}
          </div>
        </div>

        {/* 지도 + 순위 목록 */}
        <div style={{ flex: 1, display: 'flex', gap: 10, minHeight: 0 }}>
          <div style={{ flex: '1 1 60%', minWidth: 0, position: 'relative', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)', boxShadow: 'none' }}>
            {MapContainer}
          </div>
          <div style={{ flex: '0 0 300px', display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--bg-card)', borderRadius: 10, padding: '12px 10px 12px 12px', border: '1px solid var(--border)', boxShadow: 'none', minHeight: 0, overflow: 'hidden' }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-sub)', letterSpacing: '0.05em', flexShrink: 0 }}>
              방문 기회도 순위 (높을수록 방문 적합)
            </div>
            <RegionList
              regions={regions}
              loading={loading}
              highlightedRegion={highlightedRegion}
              colorRules={colorRules}
              period={DEFAULT_PERIOD}
              onItemClick={handleListClick}
            />
          </div>
        </div>
        <RegionColorSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)}
          onSaved={fetchColorRules} rulesUrl={api?.rulesUrl} />
      </div>
    );
  }

  // ── 모바일 레이아웃 (탭 방식) ─────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)', overflow: 'hidden' }}>

      {/* 모바일 헤더 — 두 줄: ① 제목 ↺ … ⚙️ ② 분류 … 지도/목록 (미남님 2026-10-30) */}
      <div style={{ flexShrink: 0, padding: '10px 12px 8px', background: 'var(--bg-card)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          {titleWithReset(18)}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {adminMode && canManage && (
              <button type="button" style={stdOutlineBtn} onClick={() => setSettingsOpen(true)}
                title="지역 색상 기준 설정" aria-label="방문빈도 설정">⚙️</button>
            )}
            {loading && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>로딩 중...</span>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          {CategoryTabs}
          {/* 지도 / 목록 탭 버튼 */}
          <div style={{ ...stdTabGroup, flexShrink: 0 }}>
            {[{ key: 'map', label: '🗺️ 지도' }, { key: 'list', label: '📋 목록' }].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setMobileTab(key)}
                style={stdTabBtn(mobileTab === key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 콘텐츠 — 지도/목록 둘 다 absolute 마운트 (visibility로 제어) */}
      <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>

        {/* 지도 탭 */}
        <div style={{ position: 'absolute', inset: 0, visibility: mobileTab === 'map' ? 'visible' : 'hidden' }}>
          {MapContainer}

          {/* 전국지도 오버레이
              - 열기/닫기 애니메이션: 오버레이 div의 scale(0→1) CSS transition
              - 이미지 pan/zoom: 브라우저 뷰포트 확대 없이 CSS transform만 적용
                (overlayDivRef에 passive:false 터치 핸들러 등록 → 브라우저 줌 차단)
              - 버튼은 position:fixed → 뷰포트와 무관하게 항상 화면 기준 고정 */}
          <div
            ref={overlayDivRef}
            style={{
              position: 'absolute', inset: 0, zIndex: 999,
              pointerEvents: mapOverlayActive ? 'auto' : 'none',
              transformOrigin: 'calc(100% - 52px) 73px',
              transform: mapOverlayActive ? 'scale(1)' : 'scale(0)',
              opacity:   mapOverlayActive ? 1 : 0,
              transition: mapOverlayActive
                ? 'transform 0.28s cubic-bezier(0.34,1.4,0.64,1), opacity 0.18s'
                : 'transform 0.22s cubic-bezier(0.4,0,1,1), opacity 0.18s',
              borderRadius: mapOverlayActive ? 0 : '50%',
              overflow: 'hidden',
              background: 'var(--bg-card)',
            }}
          >
            {/* transformOrigin: 0 0 — imgTransform의 x,y,scale 직접 적용 */}
            <img
              src="/korea-map-v2.png"
              alt="전국지도"
              style={{
                position: 'absolute', top: 0, left: 0,
                width: '100%', height: '100%',
                display: 'block',
                transformOrigin: '0 0',
                transform: `translate(${imgTransform.x}px, ${imgTransform.y}px) scale(${imgTransform.scale})`,
              }}
            />
          </div>

          {/* 전국지도 버튼 — position:fixed 하드코딩
              지도 패닝/줌, 이미지 핀치줌/패닝 모두 버튼 위치·크기 영향 없음 */}
          <button
            onClick={toggleOverlay}
            style={{
              position: 'fixed', top: mapBtnTop, right: 33, zIndex: 1000, // 지도 위쪽 + 7(위 useLayoutEffect)
              width: 42, height: 42, borderRadius: '50%', border: 'none',
              fontSize: 22, cursor: 'pointer',
              background: mapOverlayActive ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.95)',
              boxShadow: '0 3px 10px rgba(0,0,0,0.22)', backdropFilter: 'blur(4px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              lineHeight: 1, padding: 0, userSelect: 'none', WebkitUserSelect: 'none',
              transition: 'background 0.2s',
            }}
          >
            🗺️
          </button>
        </div>

        {/* 목록 탭 */}
        {mobileTab === 'list' && (
          <div style={{ position: 'absolute', inset: 0, overflowY: 'auto', background: 'var(--bg-subtle)', padding: '10px 10px', display: 'flex', flexDirection: 'column', gap: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6 }}>
              방문 기회도 순위 (높을수록 방문 적합)
            </div>
            <RegionList
              regions={regions}
              loading={loading}
              highlightedRegion={highlightedRegion}
              colorRules={colorRules}
              period={DEFAULT_PERIOD}
              onItemClick={handleListClick}
            />
          </div>
        )}
      </div>
        <RegionColorSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)}
          onSaved={fetchColorRules} rulesUrl={api?.rulesUrl} />
    </div>
  );
}
